import { Component, signal } from '@angular/core';
import { AppRoutes } from 'src/app/core/models/routes.model';
import { BaseComponent } from 'src/app/core/components/base-component/base-component.component';
import { AuthService } from 'src/app/core/services/auth.service';

/** Secondi prima del rientro automatico; allineato a `--logout-duration`. */
const REDIRECT_SECONDS = 5;

/**
 * Rotta `/auth/login`: con l'accesso gestito interamente da Keycloak (il
 * bottone "Accedi" del topbar porta direttamente al suo form), qui non serve
 * più una schermata di login. La rotta resta però la destinazione del logout,
 * quindi la pagina conferma la chiusura sessione e riporta alla vetrina, che è
 * pubblica.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent extends BaseComponent {
  readonly AppRoutes = AppRoutes;
  readonly redirectSeconds = REDIRECT_SECONDS;
  readonly secondsLeft = signal(REDIRECT_SECONDS);

  /** Rotta di destinazione: valorizzata quando si arriva qui da una rotta protetta. */
  private returnUrl: string | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly authService: AuthService) {
    super();
  }

  override ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    // Sessione già valida: non c'è nulla da confermare su questa pagina.
    if (this.authService.isAuthenticated()) {
      void this.router.navigateByUrl(this.returnUrl ?? this.defaultLandingUrl);
      return;
    }

    this.startCountdown();
  }

  override ngOnDestroy(): void {
    this.clearCountdown();
    super.ngOnDestroy();
  }

  goToPlans(): void {
    this.clearCountdown();
    void this.router.navigateByUrl(this.defaultLandingUrl);
  }

  /** Uscita verso un'altra pagina: il rientro automatico non deve più scattare. */
  stopCountdown(): void {
    this.clearCountdown();
  }

  async loginWithKeycloak(): Promise<void> {
    this.clearCountdown();
    await this.authService.login(this.returnUrl ?? undefined);
  }

  private startCountdown(): void {
    this.countdownTimer = setInterval(() => {
      const remaining = this.secondsLeft() - 1;
      this.secondsLeft.set(Math.max(remaining, 0));

      if (remaining <= 0) {
        this.goToPlans();
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer != null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private get defaultLandingUrl(): string {
    return `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${AppRoutes.DASHBOARD}`;
  }
}
